import { z } from 'zod';

import { CONTENT_SCHEMA_VERSION, WRITING_CATEGORIES } from './registry.ts';

const slugPattern = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const isoDatePattern = /^\d{4}-\d{2}-\d{2}$/;

export const contentErrorCodes = {
  SCHEMA_INVALID: 'schema_invalid',
  DUPLICATE_ID: 'duplicate_id',
  DUPLICATE_ROUTE: 'duplicate_route',
  DUPLICATE_SLUG: 'duplicate_slug',
  MDX_POLICY: 'mdx_policy',
  ASSET_MISSING: 'asset_missing',
  LINK_INVALID: 'link_invalid',
  CATEGORY_INVALID: 'category_invalid',
} as const;

export type ContentErrorCode =
  (typeof contentErrorCodes)[keyof typeof contentErrorCodes];

export class ContentValidationError extends Error {
  readonly code: ContentErrorCode;
  readonly sourcePath: string;
  readonly fieldPath?: string;

  constructor(
    message: string,
    options: {
      code: ContentErrorCode;
      sourcePath: string;
      fieldPath?: string;
    }
  ) {
    super(message);
    this.name = 'ContentValidationError';
    this.code = options.code;
    this.sourcePath = options.sourcePath;
    this.fieldPath = options.fieldPath;
  }
}

const httpUrlSchema = z
  .string()
  .url()
  .refine(
    (value) => value.startsWith('http://') || value.startsWith('https://'),
    {
      message: 'External links must use http or https',
    }
  );

export const mediaAssetSchema = z
  .object({
    src: z.string().min(1),
    kind: z.enum(['image', 'video', 'poster']),
    alt: z.string(),
    decorative: z.boolean().optional(),
    width: z.number().int().positive(),
    height: z.number().int().positive(),
    caption: z.string().optional(),
    credit: z.string().optional(),
    poster: z.string().min(1).optional(),
    sources: z
      .array(
        z.object({
          src: z.string().min(1),
          type: z.string().min(1).optional(),
        })
      )
      .optional(),
    autoplay: z.boolean().optional().default(false),
  })
  .superRefine((media, ctx) => {
    const informative = !media.decorative;
    if (informative && media.alt.trim().length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Informative media requires non-empty alt text',
        path: ['alt'],
      });
    }
    if (media.kind === 'video' && !media.poster) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Video media requires a poster',
        path: ['poster'],
      });
    }
  });

export const seoArtifactSchema = z.object({
  title: z.string().min(1),
  description: z.string().min(1),
  canonicalUrl: z.string().url(),
  robots: z.enum(['index,follow', 'noindex,nofollow']),
  openGraph: z.object({
    type: z.string().min(1),
    title: z.string().min(1),
    description: z.string().min(1),
    url: z.string().url(),
    image: z.string().url().optional(),
  }),
  twitter: z.object({
    card: z.string().min(1),
    title: z.string().min(1),
    description: z.string().min(1),
    image: z.string().url().optional(),
  }),
  sitemap: z.object({
    include: z.boolean(),
    priority: z.number().min(0).max(1).optional(),
    lastModified: z.string().regex(isoDatePattern).optional(),
  }),
});

const contentBaseSchema = z.object({
  schemaVersion: z.literal(CONTENT_SCHEMA_VERSION),
  id: z.string().min(1),
  slug: z.string().regex(slugPattern),
  title: z.string().min(1),
  description: z.string().min(1),
  status: z.enum(['draft', 'published', 'archived']),
  publishedAt: z.string().regex(isoDatePattern),
  updatedAt: z.string().regex(isoDatePattern).optional(),
  tags: z.array(z.string().min(1)).default([]),
  hero: mediaAssetSchema,
});

const projectOutcomeSchema = z.object({
  label: z.string().min(1),
  value: z.string().min(1),
  sourceNote: z.string().min(1).optional(),
});

const projectFrontmatterSchema = contentBaseSchema
  .extend({
    kind: z.literal('project'),
    detailStatus: z.enum(['index-only', 'case-study']),
    role: z.string().min(1),
    services: z.array(z.string().min(1)).default([]),
    period: z.string().optional(),
    client: z.string().optional(),
    featured: z.boolean().optional().default(false),
    featuredOrder: z.number().int().nonnegative().optional(),
    links: z
      .object({
        live: httpUrlSchema.optional(),
        github: httpUrlSchema.optional(),
      })
      .optional(),
    facts: z.array(z.string().min(1)).optional(),
    outcomes: z.array(projectOutcomeSchema).optional(),
  })
  .superRefine((value, ctx) => {
    if (value.featured && value.detailStatus !== 'case-study') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'featured requires detailStatus case-study',
        path: ['featured'],
      });
    }
    if (value.updatedAt && value.updatedAt < value.publishedAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'updatedAt must be on or after publishedAt',
        path: ['updatedAt'],
      });
    }
  });

const writingFrontmatterSchema = contentBaseSchema
  .extend({
    kind: z.literal('writing'),
    category: z.enum(WRITING_CATEGORIES),
    discussionEnabled: z.boolean().optional().default(true),
  })
  .superRefine((value, ctx) => {
    if (value.updatedAt && value.updatedAt < value.publishedAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'updatedAt must be on or after publishedAt',
        path: ['updatedAt'],
      });
    }
  });

export const projectFrontmatterInputSchema = projectFrontmatterSchema;
export const writingFrontmatterInputSchema = writingFrontmatterSchema;

export type ProjectFrontmatter = z.infer<typeof projectFrontmatterSchema>;
export type WritingFrontmatter = z.infer<typeof writingFrontmatterSchema>;

export type HeadingRecord = {
  id: string;
  text: string;
  level: number;
};

export type ProjectRecord = ProjectFrontmatter & {
  sourcePath: string;
  route?: string;
  readingMinutes?: number;
  headings?: HeadingRecord[];
  seo?: z.infer<typeof seoArtifactSchema>;
  bodyModule?: string;
};

export type WritingRecord = WritingFrontmatter & {
  sourcePath: string;
  route: string;
  readingMinutes: number;
  headings: HeadingRecord[];
  seo: z.infer<typeof seoArtifactSchema>;
  bodyModule: string;
};

export type ContentRecord = ProjectRecord | WritingRecord;

export type PublicProjectRecord = Omit<
  ProjectRecord,
  'sourcePath' | 'bodyModule'
>;

export type PublicWritingRecord = Omit<
  WritingRecord,
  'sourcePath' | 'bodyModule'
>;

export type PublicContentRecord = PublicProjectRecord | PublicWritingRecord;

export type ContentManifest = {
  schemaVersion: typeof CONTENT_SCHEMA_VERSION;
  generatedAt: null;
  projects: ProjectRecord[];
  writing: WritingRecord[];
};

export type PublicContentManifest = {
  schemaVersion: typeof CONTENT_SCHEMA_VERSION;
  projects: PublicProjectRecord[];
  writing: PublicWritingRecord[];
};

export function toPublicProject(record: ProjectRecord): PublicProjectRecord {
  const { sourcePath, bodyModule, ...rest } = record;
  void sourcePath;
  void bodyModule;
  return rest;
}

export function toPublicWriting(record: WritingRecord): PublicWritingRecord {
  const { sourcePath, bodyModule, ...rest } = record;
  void sourcePath;
  void bodyModule;
  return rest;
}

export function toPublicManifest(
  manifest: ContentManifest
): PublicContentManifest {
  return {
    schemaVersion: manifest.schemaVersion,
    projects: manifest.projects
      .filter((record) => record.status === 'published')
      .map((record) => toPublicProject(record)),
    writing: manifest.writing
      .filter((record) => record.status === 'published')
      .map((record) => toPublicWriting(record)),
  };
}

export function normalizeTags(tags: string[]): string[] {
  const seen = new Set<string>();
  const normalized: string[] = [];
  for (const tag of tags) {
    const value = tag.trim().toLowerCase();
    if (!value || seen.has(value)) continue;
    seen.add(value);
    normalized.push(value);
  }
  return normalized.sort((left, right) => left.localeCompare(right));
}

export function normalizeServices(services: string[]): string[] {
  return normalizeTags(services);
}

export function deriveProjectRoute(
  slug: string,
  detailStatus: 'index-only' | 'case-study'
) {
  return detailStatus === 'case-study' ? `/projects/${slug}` : undefined;
}

export function deriveWritingRoute(slug: string) {
  return `/blog/${slug}`;
}
